import Banner from "@/components/common/Banner";
import { useStatusQuery } from "../api/useStatusQuery";
import { AlertTriangle } from "lucide-react";
import { useEffect, useState } from "react";

const FALLBACK_STATUS_PAGE_URL = "https://status.patient.watch/";
const DISMISSED_KEY = "statusBannerDismissed";
const DISMISSED_INCIDENT_KEY = "statusBannerDismissedIncidentId";

const ISSUE_STATUSES = new Set([
    "down",
    "validating",
    "downtime",
    "degraded",
    "degraded_performance",
    "partial_outage",
    "major_outage",
]);

export function StatusBanner() {
    const { data: statusData, isLoading } = useStatusQuery();
    const [isDismissed, setIsDismissed] = useState(false);
    const statusPageUrl = statusData?.statusPageUrl || FALLBACK_STATUS_PAGE_URL;

    // Track dismissals by first degraded resource (or aggregate state)
    const currentIncidentId =
        statusData?.monitors?.find((m) => ISSUE_STATUSES.has(m.status))?.id ||
        (statusData?.hasIssues ? statusData.aggregateState || "issue" : null);

    // Check if there are ongoing issues (any monitor is down or validating)
    const hasOngoingIncident = statusData?.hasIssues === true;

    // Reset dismissal if it's a new incident
    useEffect(() => {
        if (typeof window === "undefined") return;

        const dismissedIncidentId = localStorage.getItem(DISMISSED_INCIDENT_KEY);
        if (dismissedIncidentId && dismissedIncidentId !== currentIncidentId) {
            // New incident, reset dismissal
            setIsDismissed(false);
            localStorage.removeItem(DISMISSED_KEY);
            localStorage.removeItem(DISMISSED_INCIDENT_KEY);
        } else if (dismissedIncidentId === currentIncidentId) {
            // Same incident, keep dismissed
            setIsDismissed(true);
        } else if (!hasOngoingIncident) {
            // No ongoing incident, reset dismissals
            setIsDismissed(false);
            localStorage.removeItem(DISMISSED_KEY);
            localStorage.removeItem(DISMISSED_INCIDENT_KEY);
        }
    }, [currentIncidentId, hasOngoingIncident]);

    if (isLoading || !hasOngoingIncident || isDismissed) {
        return null;
    }

    const handleDismiss = () => {
        setIsDismissed(true);
        if (typeof window !== "undefined") {
            localStorage.setItem(DISMISSED_KEY, "true");
            if (currentIncidentId) {
                localStorage.setItem(DISMISSED_INCIDENT_KEY, currentIncidentId);
            }
        }
    };

    return (
        <Banner
            show={!isDismissed}
            setShow={handleDismiss}
            className="bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800"
        >
            <div className="flex justify-start items-center gap-2 w-full">
                <AlertTriangle size={20} className="flex-shrink-0 text-amber-600 dark:text-amber-400" />
                <div className="flex sm:flex-row flex-col sm:items-center gap-1 sm:gap-2">
                    <span className="font-medium text-amber-800 dark:text-amber-200 text-sm">
                        {statusData?.description || "Ongoing issue detected"}
                    </span>
                    <a
                        href={statusPageUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-amber-700 dark:text-amber-300 text-sm hover:underline"
                    >
                        View status page
                    </a>
                </div>
            </div>
        </Banner>
    );
}
